<template lang="pug">
div
  v-card-text
    .d-flex.justify-space-between.align-end.w-100.pr-2
      h2
        | Ubicación Potencial Comercial
        ShowInfoBtn(@click="$refs.infoAlert.showAlert()")
  v-card-text
    InfoAlert(:message="alertMessage" ref="infoAlert")
    div(id="leafletmap" style="width: 100%; height: 70vh; min-height: 420px; border-radius: 8px; z-index: 0")
</template>

<script>
import { mapActions } from 'vuex'
import InfoAlert from '../../common/InfoAlert.vue'
import ShowInfoBtn from '../../common/ShowInfoBtn.vue'

// Mapa sin API key: Leaflet + OpenStreetMap (gratis). Se cargan desde CDN.
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
const LEAFLET_HEAT_JS = 'https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js'

function loadCss(href) {
  if (document.querySelector(`link[href="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded) return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', reject)
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.addEventListener('load', () => { s.dataset.loaded = '1'; resolve() })
    s.addEventListener('error', reject)
    document.head.appendChild(s)
  })
}

export default {
  components: {
    InfoAlert,
    ShowInfoBtn
  },
  props: {
    filters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    projection: {
      type: Number,
      default: null,
    },
    marketSize: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      map: null,
      heatLayer: null,
      markersLayer: null,
      legend: null,
      alertMessage: `
      <p>
Adicionalmente, puedes aprovechar la visualización por <b>zonas de calor en el mapa</b> para identificar las áreas con mayor potencial comercial. Esta herramienta te <b>facilitará la toma de decisiones estratégicas</b> y la <b>optimización</b> de la distribución de recursos <b>basada en datos</b> precisos y localizados.
</p>`,
    }
  },
  async mounted() {
    loadCss(LEAFLET_CSS)
    await loadScript(LEAFLET_JS)
    await loadScript(LEAFLET_HEAT_JS)
    this.initMap()
    await this.updateHeatMap()
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  watch: {
    filters: {
      handler() {
        if (this.map) this.updateHeatMap()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('commercial_projection', ['getHeatMapChart']),
    initMap() {
      // Centro aproximado en la zona centro-sur de Chile (Biobío).
      this.map = window.L.map('leafletmap').setView([-37.0, -72.6], 7)
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 18,
        crossOrigin: true, // permite capturar el mapa en el PDF del reporte
      }).addTo(this.map)
      // El contenedor puede montarse sin tamaño (dentro de pestañas): recalcular.
      this.$nextTick(() => setTimeout(() => this.map && this.map.invalidateSize(), 300))
    },
    money(v) {
      return '$' + Math.round(v || 0).toLocaleString('es-CL')
    },
    // Escala secuencial azul: claro (potencial bajo) -> azul oscuro (potencial
    // alto), igual que la leyenda. Monocroma para ser legible en daltonismo
    // (la anterior rojo->verde era la peor combinación para daltónicos).
    colorFor(frac) {
      const stops = [
        [0.0, [227, 236, 251]], // #E3ECFB azul muy claro
        [0.4, [149, 178, 235]], // #95B2EB azul claro
        [0.6, [85, 117, 223]],  // #5575DF azul marca
        [0.8, [58, 82, 168]],   // #3A52A8 azul
        [1.0, [30, 45, 105]],   // #1E2D69 azul oscuro
      ]
      let a = stops[0], b = stops[stops.length - 1]
      for (let i = 0; i < stops.length - 1; i++) {
        if (frac >= stops[i][0] && frac <= stops[i + 1][0]) { a = stops[i]; b = stops[i + 1]; break }
      }
      const t = b[0] === a[0] ? 0 : (frac - a[0]) / (b[0] - a[0])
      const c = a[1].map((ch, i) => Math.round(ch + (b[1][i] - ch) * t))
      return `rgb(${c[0]},${c[1]},${c[2]})`
    },
    breakdownHtml(title, rows) {
      if (!rows || !rows.length) return ''
      const items = rows.map(r => `<div style="display:flex;justify-content:space-between;gap:12px">
        <span style="max-width:150px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${r.name}</span>
        <b>${this.money(r.total)}</b></div>`).join('')
      return `<div style="margin-top:6px"><div style="font-weight:600;color:#5575DF">${title}</div>${items}</div>`
    },
    addLegend(maxTotal) {
      const L = window.L
      if (this.legend) { this.map.removeControl(this.legend); this.legend = null }
      const legend = L.control({ position: 'bottomleft' })
      legend.onAdd = () => {
        const div = L.DomUtil.create('div')
        div.style.cssText = 'background:#fff;padding:8px 10px;border-radius:8px;box-shadow:0 1px 4px rgba(0,0,0,.3);font:12px Poppins,sans-serif'
        div.innerHTML = `<div style="font-weight:600;margin-bottom:4px">Potencial (CLP)</div>
          <div style="display:flex;align-items:center;gap:6px">
            <span>${this.money(0)}</span>
            <span style="width:120px;height:12px;border-radius:6px;display:inline-block;
              background:linear-gradient(90deg,#E3ECFB,#95B2EB,#5575DF,#3A52A8,#1E2D69)"></span>
            <span>${this.money(maxTotal)}</span>
          </div>`
        return div
      }
      legend.addTo(this.map)
      this.legend = legend
    },
    async updateHeatMap() {
      const params = {
        ...this.filters,
        projection: this.projection || this.$route.params.id
      }
      const { data, error } = await this.getHeatMapChart(params)
      if (error || !data) return

      const L = window.L
      const points = data
        .filter(d => d.latitude != null && d.longitude != null)
        .map(d => ({
          lat: d.latitude, lng: d.longitude,
          weight: d.weight || 0,
          total: d.total != null ? d.total : Math.round((d.weight || 0) * 1000),
          commune: d.commune || '',
          byCustomer: d.by_customer || [],
          byProduct: d.by_product || [],
        }))

      // Limpiar capas previas
      if (this.heatLayer) { this.map.removeLayer(this.heatLayer); this.heatLayer = null }
      if (this.markersLayer) { this.map.removeLayer(this.markersLayer); this.markersLayer = null }
      if (!points.length) return

      const maxWeight = Math.max(...points.map(p => p.weight)) || 1
      const maxTotal = Math.max(...points.map(p => p.total)) || 1

      // El contenedor puede tener ancho 0 al montarse dentro de la pestaña.
      // leaflet.heat llama getImageData sobre un canvas 0x0 y lanza IndexSizeError,
      // así que esperamos a que el mapa tenga tamaño real antes de dibujar.
      const render = () => {
        if (!this.map) return
        this.map.invalidateSize()
        if (this.map.getSize().x === 0) {
          setTimeout(render, 200)
          return
        }

        try {
          this.heatLayer = L.heatLayer(
            points.map(p => [p.lat, p.lng, p.weight]),
            {
              radius: 45, blur: 35, max: maxWeight, minOpacity: 0.4,
              // azul oscuro = mayor potencial, azul claro = menor (igual que la leyenda)
              gradient: { 0.15: '#E3ECFB', 0.4: '#95B2EB', 0.6: '#5575DF', 0.8: '#3A52A8', 1.0: '#1E2D69' },
            }
          ).addTo(this.map)
        } catch (e) {
          this.heatLayer = null
        }

        // Marcadores: tamaño y color según potencial; popup con desglose por cliente y producto
        this.markersLayer = L.layerGroup()
        points.forEach(p => {
          const frac = p.weight / maxWeight
          const radius = 8 + 20 * frac
          const popup = `<div style="min-width:200px;font:13px Poppins,sans-serif">
            <div style="font-weight:700;font-size:14px">${p.commune || 'Sin comuna'}</div>
            <div>Potencial: <b>${this.money(p.total)}</b></div>
            ${this.breakdownHtml('Por cliente', p.byCustomer)}
            ${this.breakdownHtml('Por producto', p.byProduct)}
          </div>`
          L.circleMarker([p.lat, p.lng], {
            radius,
            color: '#2c3e50',
            fillColor: this.colorFor(frac),
            fillOpacity: 0.85,
            weight: 1,
          })
            .bindPopup(popup, { maxWidth: 300 })
            .bindTooltip(`${p.commune}: ${this.money(p.total)}`, { direction: 'top' })
            .addTo(this.markersLayer)
        })
        this.markersLayer.addTo(this.map)
        this.addLegend(maxTotal)

        // Ajustar el encuadre a los puntos
        const group = L.featureGroup(this.markersLayer.getLayers())
        if (group.getBounds().isValid()) {
          this.map.fitBounds(group.getBounds().pad(0.3))
        }
      }

      this.$nextTick(() => setTimeout(render, 150))
    }
  }
}
</script>
